import { useState } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Shield, Users, Scale, CheckCircle, Phone, ArrowRight, Award, Clock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import TestimonialsSection from "@/components/TestimonialsSection";
import { VideoTestimonialsSection } from "@/components/VideoTestimonialsSection";
import AIReadableSection from "@/components/AIReadableSection";
import HeroSection from "@/components/HeroSection";
const heroImage = "/images/hero-law-office.jpg";
import immigrationHero from "@/assets/immigration-hero.jpg";
import criminalDefenseHero from "@/assets/criminal-defense-hero.jpg";
import personalInjuryHero from "@/assets/personal-injury-hero.jpg";
import familyLawHero from "@/assets/family-law-hero.jpg";
import taxLawHero from "@/assets/tax-law-hero.jpg";
import probateEstateHero from "@/assets/probate-estate-hero.jpg";
import realEstateHero from "@/assets/real-estate-hero.jpg";
import businessLawHero from "@/assets/business-law-hero.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { homepageSchemas, generateFAQSchema } from "@/lib/seo-schemas-enhanced";

// Homepage FAQs for SEO and AI Search
const homepageFAQs = [
  {
    question: "What legal services does Daniel Albert Law Firm offer?",
    answer: "Daniel Albert Law Firm provides comprehensive legal services including Immigration Law (visas, green cards, citizenship, deportation defense), Criminal Defense (DWI, drug charges, assault), Personal Injury (auto accidents, medical malpractice), Family Law (divorce, custody, adoption), Business Law, Real Estate Law, Tax Law, and Probate & Estate Planning."
  },
  {
    question: "Does the firm offer services in languages other than English?",
    answer: "Yes, Daniel Albert Law Firm offers trilingual legal services in English, Spanish, and Vietnamese. This ensures clear communication and understanding for clients from diverse backgrounds."
  },
  {
    question: "How can I schedule a consultation?",
    answer: "You can schedule a consultation by calling (832) 930-3059 for English, (832) 356-7254 for Spanish, or by booking online through our website at danielalbertlaw.com/appointment."
  },
  {
    question: "What areas in Houston does the firm serve?",
    answer: "We serve all of Houston and surrounding areas including Downtown, Midtown, Heights, Montrose, River Oaks, West University, Medical Center, Memorial, Uptown/Galleria, Energy Corridor, Katy, Sugar Land, The Woodlands, Cypress, and Spring Branch."
  },
  {
    question: "What are the firm's office hours?",
    answer: "Our office is open Monday through Friday from 9:00 AM to 6:00 PM, and Saturday from 10:00 AM to 2:00 PM. We are closed on Sundays."
  }
];

// AI-readable summary points for homepage
const aiSummaryPoints = {
  en: [
    "Houston-based law firm with 20+ years of experience serving Texas communities",
    "Trilingual legal services in English, Spanish, and Vietnamese",
    "8 practice areas: Immigration, Criminal Defense, Personal Injury, Family Law, Business, Real Estate, Tax, and Probate",
    "Serving greater Houston area including Downtown, Katy, Sugar Land, The Woodlands, and more",
    "Free initial consultation available - Call (832) 930-3059"
  ],
  es: [
    "Firma de abogados en Houston con más de 20 años de experiencia sirviendo a comunidades de Texas",
    "Servicios legales trilingües en inglés, español y vietnamita",
    "8 áreas de práctica: Inmigración, Defensa Criminal, Lesiones Personales, Derecho Familiar, Empresarial, Bienes Raíces, Fiscal y Sucesiones",
    "Sirviendo el área metropolitana de Houston incluyendo Downtown, Katy, Sugar Land, The Woodlands y más",
    "Consulta inicial gratuita disponible - Llame al (832) 356-7254"
  ],
  vi: [
    "Văn phòng luật sư tại Houston với hơn 20 năm kinh nghiệm phục vụ cộng đồng Texas",
    "Dịch vụ pháp lý ba ngôn ngữ: Tiếng Anh, Tiếng Tây Ban Nha và Tiếng Việt",
    "8 lĩnh vực: Di trú, Bào chữa Hình sự, Thương tích Cá nhân, Luật Gia đình, Doanh nghiệp, Bất động sản, Thuế và Di sản",
    "Phục vụ khu vực Houston bao gồm Downtown, Katy, Sugar Land, The Woodlands và nhiều nơi khác",
    "Tư vấn miễn phí - Gọi (832) 930-3059"
  ]
};

const Index = () => {
  const { t, language } = useLanguage();
  
  const practiceAreas = [
    {
      icon: Globe,
      title: t('practices.immigration'),
      description: "Comprehensive immigration services including asylum, naturalization, visas, and deportation defense.",
      link: "/practice-areas/immigration-services",
    },
    {
      icon: Shield,
      title: t('practices.criminal'),
      description: "Aggressive defense for assault, DWI, drug charges, and all criminal matters.",
      link: "/practice-areas/criminal-defense",
    },
    {
      icon: Scale,
      title: t('practices.injury'),
      description: "Fighting for maximum compensation in auto accidents, medical malpractice, and industrial injuries.",
      link: "/practice-areas/personal-injury",
    },
    {
      icon: Users,
      title: t('practices.family'),
      description: "Compassionate representation in divorce, custody, adoption, and CPS defense.",
      link: "/practice-areas/family-law",
    },
    {
      icon: Scale,
      title: t('practices.tax'),
      description: "Skilled tax litigation, debt resolution, and planning services.",
      link: "/practice-areas/tax-law",
    },
    {
      icon: Shield,
      title: t('practices.probate'),
      description: "Estate planning, probate administration, and guardianship services.",
      link: "/practice-areas/probate-estate",
    },
    {
      icon: Users,
      title: t('practices.realestate'),
      description: "Real estate closings, transactions, and dispute resolution.",
      link: "/practice-areas/real-estate-law",
    },
    {
      icon: Globe,
      title: t('practices.business'),
      description: "Business formation, contracts, and commercial legal services.",
      link: "/practice-areas/business-law",
    },
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: t('home.why.team'),
      description: t('home.why.team.desc'),
    },
    {
      icon: Users,
      title: t('home.why.bilingual'),
      description: t('home.why.bilingual.desc'),
    },
    {
      icon: Clock,
      title: t('home.why.available'),
      description: t('home.why.available.desc'),
    },
    {
      icon: CheckCircle,
      title: t('home.why.results'),
      description: t('home.why.results.desc'),
    },
  ];

  const process = [
    {
      step: "01",
      title: t('home.process.step1'),
      description: t('home.process.step1.desc'),
    },
    {
      step: "02",
      title: t('home.process.step2'),
      description: t('home.process.step2.desc'),
    },
    {
      step: "03",
      title: t('home.process.step3'),
      description: t('home.process.step3.desc'),
    },
    {
      step: "04",
      title: t('home.process.step4'),
      description: t('home.process.step4.desc'),
    },
  ];

  // Combine homepage schemas with FAQ schema
  const combinedSchemas = [...homepageSchemas, generateFAQSchema(homepageFAQs)];
  const summaryPoints = aiSummaryPoints[language] || aiSummaryPoints.en;

  return (
    <div className="min-h-screen">
      <SEO
        title="Top Immigration Lawyer Houston | Daniel Albert Law Firm"
        description="Leading immigration lawyer in Houston, TX specializing in asylum, naturalization, visas & deportation defense. Trilingual services (English/Spanish/Vietnamese). Call (832) 930-3059"
        keywords="immigration lawyer Houston, Houston immigration attorney, asylum lawyer Houston, deportation defense Houston, naturalization lawyer Houston, abogado de inmigracion Houston, luật sư di trú Houston"
        schema={combinedSchemas}
      />
      <Header />

      <main>
      <HeroSection
        imageSrc={heroImage}
        imageAlt="Daniel Albert Law Office - Houston Immigration Attorney"
        height="700px"
        priority={true}
      >
        <div className="max-w-4xl text-primary-foreground">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg leading-tight">
            {t('home.hero.title')}
          </h1>
          <p className="text-xl mb-8 opacity-95 drop-shadow-md">
            {t('home.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" variant="hero" className="text-lg px-8">
              <LocalizedLink to="/appointment">
                {t('home.hero.consultation')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </LocalizedLink>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary-dark bg-white/10 backdrop-blur-sm text-lg px-8">
                <Phone className="mr-2 h-5 w-5" />
                {t('home.hero.call')}: (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </HeroSection>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('home.areas.title')}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('home.areas.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {practiceAreas.map((area, index) => {
              const images = [
                immigrationHero,
                criminalDefenseHero,
                personalInjuryHero,
                familyLawHero,
                taxLawHero,
                probateEstateHero,
                realEstateHero,
                businessLawHero,
              ];
              const image = images[index];
              
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={image} 
                      alt={`${area.title} - Daniel Albert Law`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                      width="400"
                      height="225"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-primary-foreground drop-shadow-lg">{area.title}</h3>
                    </div>
                  </div>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{area.description}</p>
                    <div className="space-y-2">
                      <Button asChild variant="default" size="sm" className="w-full text-sm">
                        <LocalizedLink to={area.link}>
                          {area.title}
                        </LocalizedLink>
                      </Button>
                    <Button asChild variant="consultation" size="sm" className="w-full text-sm">
                      <LocalizedLink to="/appointment">
                        {t('cta.consultation.button')}
                      </LocalizedLink>
                    </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Button asChild variant="outline" size="lg" className="border-2 border-primary hover:bg-primary hover:text-primary-foreground font-semibold">
              <a href="/practice-areas">{t('home.areas.viewall')}</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('home.why.title')}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('home.why.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center group">
                <div className="h-20 w-20 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-6 shadow-gold group-hover:scale-110 transition-transform">
                  <item.icon className="h-10 w-10 text-accent-foreground" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-muted-foreground text-lg">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-background border-y border-accent/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3">Awards & Recognition</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Trusted by thousands of clients and recognized for excellence in legal services
            </p>
          </div>
          <div className="flex justify-center">
            <img 
              src="/images/awards-badges.png" 
              alt="Awards and Recognition from Expertise.com and Avvo" 
              className="max-w-full h-auto w-auto max-h-64 object-contain"
              width="484"
              height="256"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('home.process.title')}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('home.process.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-muted-foreground mb-4">{item.step}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 text-accent">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-Readable Quick Summary Section */}
      <AIReadableSection 
        title={language === 'es' ? 'Resumen Rápido' : language === 'vi' ? 'Tóm Tắt Nhanh' : 'Quick Summary'} 
        summary={summaryPoints}
      />

      <TestimonialsSection />

      <VideoTestimonialsSection />

      <CTASection
        title={t('home.cta.ready')}
        description={t('home.cta.ready.desc')}
      />
      </main>

      <Footer />
    </div>
  );
};

export default Index;
